import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Database, ArrowClockwise, CheckCircle, XCircle, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Admin-only card to re-run the post-deploy migration sequence.
 *
 * Calls POST /api/admin/run-migrations with the required `confirm: "RUN"`
 * body. Displays per-migration duration + captured log so the user can
 * see exactly what each step did. Hidden for non-AZAI users (the
 * backend enforces a 403, but we hide the card client-side too so
 * agency users never see the button).
 */
const MigrationsCard = ({ currentUser }) => {
  const isAzaiAdmin = currentUser?.entity_type === 'azai';
  const [migrations, setMigrations] = useState([]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [showLogFor, setShowLogFor] = useState(null);

  // Fetch the registry on mount so we can show users which migrations
  // are available BEFORE they click run (preview vs blind execution).
  useEffect(() => {
    if (!isAzaiAdmin) return;
    api.get('/admin/migrations')
      .then((res) => setMigrations(res.data?.migrations || []))
      .catch(() => { /* silent — card stays usable, just no preview list */ });
  }, [isAzaiAdmin]);

  if (!isAzaiAdmin) return null;

  const handleRun = async () => {
    setRunning(true);
    setResults(null);
    try {
      const { data } = await api.post('/admin/run-migrations', { confirm: 'RUN' });
      setResults(data);
      if (data.overall_status === 'ok') {
        toast.success(`All ${data.ran_count} migrations ran successfully (${data.total_duration_ms}ms)`);
      } else {
        toast.error(`Migrations aborted — see card for details`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run migrations');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="migrations-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Database size={18} className="text-indigo-600" />
              Database Migrations
            </CardTitle>
            <CardDescription className="mt-1">
              Re-run the post-deploy migration sequence (idempotent — safe after every redeploy).
            </CardDescription>
          </div>
          <Button
            onClick={handleRun}
            disabled={running}
            size="sm"
            className="bg-indigo-900 hover:bg-indigo-800 text-white shrink-0"
            data-testid="run-migrations-btn"
          >
            <ArrowClockwise size={16} className={`mr-2 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running…' : 'Run all migrations'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Pre-run preview: list available migrations from the registry */}
        {!results ? (
          <div className="space-y-1.5">
            {migrations.map((m, i) => (
              <div key={m.name} className="flex items-center gap-2 text-xs text-slate-600">
                <span className="font-mono text-slate-400 w-5">{i + 1}.</span>
                <span className="font-mono">{m.name}</span>
              </div>
            ))}
            {migrations.length === 0 && (
              <p className="text-xs text-slate-500">Loading migration list…</p>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="migrations-results">
            <div
              className={`text-xs font-medium ${
                results.overall_status === 'ok' ? 'text-emerald-700' : 'text-red-700'
              }`}
            >
              {results.overall_status === 'ok' ? '✓' : '✗'} Overall: {results.overall_status} —{' '}
              {results.ran_count} migration(s) in {results.total_duration_ms}ms
            </div>
            {results.results.map((r) => (
              <div
                key={r.name}
                className={`rounded border px-3 py-2 text-xs ${
                  r.status === 'ok'
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-red-200 bg-red-50'
                }`}
                data-testid={`migration-result-${r.name}`}
              >
                <div className="flex items-center gap-2 font-mono">
                  {r.status === 'ok' ? (
                    <CheckCircle size={14} className="text-emerald-700 shrink-0" weight="fill" />
                  ) : (
                    <XCircle size={14} className="text-red-700 shrink-0" weight="fill" />
                  )}
                  <span className="font-medium flex-1">{r.name}</span>
                  <span className="text-slate-500">{r.duration_ms}ms</span>
                  {r.log && (
                    <button
                      type="button"
                      onClick={() => setShowLogFor(showLogFor === r.name ? null : r.name)}
                      className="text-indigo-700 hover:underline ml-1"
                    >
                      <CaretRight
                        size={12}
                        className={`transition-transform ${showLogFor === r.name ? 'rotate-90' : ''}`}
                      />
                    </button>
                  )}
                </div>
                {r.error && (
                  <pre className="mt-1 text-red-700 whitespace-pre-wrap">{r.error}</pre>
                )}
                {showLogFor === r.name && r.log && (
                  <pre className="mt-2 bg-white border border-slate-200 rounded p-2 text-[10px] text-slate-700 max-h-40 overflow-y-auto whitespace-pre-wrap">
                    {r.log}
                  </pre>
                )}
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResults(null)}
              className="text-xs"
            >
              Run again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MigrationsCard;
